import {Injectable} from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import {Observable, of, Subject, throwError} from 'rxjs';
import {catchError, filter, switchMap, take} from 'rxjs/operators';
import {TokenService} from './token.service';
import {TokenDto} from '../common/dtos/tokenDto';

@Injectable({
  providedIn: 'root'
})
export class ApiClient {
  private isRefreshing = false;
  private refreshSubject = new Subject<boolean>();

  constructor(private http: HttpClient, private authService: TokenService) {
  }

  private getHeaders(): HttpHeaders {
    let headers = new HttpHeaders();
    const token = this.authService.getToken();
    if (token) {
      headers = headers.set('Authorization', `Bearer ${token}`);
    }
    return headers;
  }

  private ensureValidToken(): Observable<void> {
    if (!this.authService.tokenIsExpired()) {
      return of(void 0);
    }
    if (this.isRefreshing) {
      return this.refreshSubject.pipe(
        filter(done => done),
        take(1),
        switchMap(() => of(void 0))
      );
    }
    if (this.authService.refreshIsExpired()) {
      this.authService.clear();
      this.authService.login();
      return throwError(() => new Error('Session expired'));
    }
    this.isRefreshing = true;
    const refreshToken = this.authService.getRefresh();
    return this.http.post<TokenDto>(`/api/user/refresh/${refreshToken}`, {}).pipe(
      switchMap((token: TokenDto) => {
        this.authService.setToken(token.token);
        this.authService.setRefresh(token.refresh);
        this.isRefreshing = false;
        this.refreshSubject.next(true);
        return of(void 0);
      }),
      catchError(err => {
        this.isRefreshing = false;
        this.refreshSubject.next(false);
        this.authService.clear();
        this.authService.login();
        return throwError(() => err);
      })
    );
  }

  // Add this overload
  public get<T>(url: string, options: {
    params?: HttpParams | { [param: string]: string | number | boolean | ReadonlyArray<string | number | boolean> },
    responseType: 'blob'
  }): Observable<Blob>;

  public get<T>(url: string, params?: HttpParams | {
    [param: string]: string | number | boolean | ReadonlyArray<string | number | boolean>;
  }): Observable<T>;

  public get(url: string, params?: HttpParams | {
    [param: string]: string | number | boolean | ReadonlyArray<string | number | boolean>;
  }): Observable<any>;

  public get<T>(url: string, paramsOrOptions?: any): Observable<any> {
    return this.ensureValidToken().pipe(
      switchMap(() => {
        // If options object with responseType is passed, use it
        if (paramsOrOptions && paramsOrOptions.responseType) {
          return this.http.get(url, {
            headers: this.getHeaders(),
            ...paramsOrOptions
          });
        }
        // Otherwise, treat as params
        return this.http.get<T>(url, {
          headers: this.getHeaders(),
          params: paramsOrOptions
        });
      })
    );
  }

  public post<T>(url: string, body: any, params?: HttpParams | {
    [param: string]: string | number | boolean | ReadonlyArray<string | number | boolean>;
  }): Observable<T> {
    return this.ensureValidToken().pipe(
      switchMap(() => this.http.post<T>(url, body, {
        headers: this.getHeaders()
      }))
    );
  }

  public patch<T>(url: string, body: any): Observable<T> {
    return this.ensureValidToken().pipe(
      switchMap(() => this.http.patch<T>(url, body, {
        headers: this.getHeaders()
      }))
    );
  }

  public delete<T>(url: string): Observable<T> {
    return this.ensureValidToken().pipe(
      switchMap(() => this.http.delete<T>(url, {
        headers: this.getHeaders()
      }))
    );
  }
}
